import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import type { PredefinedPartLabel } from '../../types';
import { getGLBUrl } from '../../services/cad.service';
import CADModelViewer from './CADModelViewer';
import RegisteredPartLabelsList from './part-label-editor/RegisteredPartLabelsList';
import { useCADPartLabelEditor } from './part-label-editor/useCADPartLabelEditor';

interface CADPartLabelEditorDialogProps {
  open: boolean;
  cadModelId?: string;
  initialLabels?: PredefinedPartLabel[];
  onApply: (labels: PredefinedPartLabel[]) => void;
  onClose: () => void;
}

const EMPTY_LABELS: PredefinedPartLabel[] = [];

const CADPartLabelEditorDialog: React.FC<CADPartLabelEditorDialogProps> = ({
  open,
  cadModelId,
  initialLabels = EMPTY_LABELS,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation();
  const modelRequiredMessage = t(
    'drbfm.cadLink.labelEditor.modelRequired',
    'CADモデルを選択してください',
  );
  const modelLoadFailedMessage = t(
    'drbfm.cadLink.labelEditor.modelLoadFailed',
    'CADモデルの取得に失敗しました',
  );

  const {
    cadModel,
    isLoadingModel,
    modelError,
    labels,
    selectedPosition,
    labelName,
    setLabelName,
    category,
    setCategory,
    note,
    setNote,
    formError,
    setFormError,
    markers,
    formattedPosition,
    handleAddLabel,
    handleDeleteLabel,
    handleModelPointClick,
    handleApply,
  } = useCADPartLabelEditor({ open, cadModelId, initialLabels, onApply, onClose });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="revy-ui flex flex-col p-0 w-[calc(100%-2rem)] max-w-[1400px] sm:max-w-[1400px] max-h-[90vh]"
        aria-describedby={undefined}
      >
        <DialogHeader className="p-6 pb-4">
          <DialogTitle id="cad-part-label-editor-title" className="revy-cjk">
            {t('drbfm.cadLink.labelEditor.title', '3Dで部位を登録')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto border-y border-border px-6 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)] gap-4 min-h-[720px] lg:min-h-[560px]">
            <div className="min-h-[420px] lg:min-h-[560px]">
              {isLoadingModel && (
                <div className="grid h-full place-items-center">
                  <Spinner />
                </div>
              )}
              {modelError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {modelError === 'model-required'
                      ? modelRequiredMessage
                      : modelLoadFailedMessage}
                  </AlertDescription>
                </Alert>
              )}
              {!isLoadingModel && !modelError && cadModel && cadModelId && (
                <CADModelViewer
                  glbUrl={getGLBUrl(cadModelId)}
                  filename={cadModel.filename}
                  markers={markers}
                  selectionMode
                  onModelPointClick={handleModelPointClick}
                />
              )}
            </div>

            <div className="flex flex-col gap-4">
              <Alert variant="info">
                <MapPin className="h-4 w-4" />
                <AlertDescription>
                  {t(
                    'drbfm.cadLink.labelEditor.instructions',
                    'モデルを回転・拡大し、部位として登録する形状をクリックしてください。',
                  )}
                </AlertDescription>
              </Alert>
              <Field label={t('drbfm.cadLink.labelEditor.position', '選択座標')}>
                {(controlProps) => (
                  <Input
                    {...controlProps}
                    value={formattedPosition}
                    readOnly
                  />
                )}
              </Field>
              <Field label={t('drbfm.cadLink.labelEditor.labelName', '部位名')} required>
                {(controlProps) => (
                  <Input
                    {...controlProps}
                    value={labelName}
                    onChange={(event) => {
                      setLabelName(event.target.value);
                      setFormError(null);
                    }}
                    placeholder={t('drbfm.cadLink.labelEditor.labelPlaceholder', '例: メインヒンジ')}
                  />
                )}
              </Field>
              <Field label={t('drbfm.cadLink.labelEditor.category', 'カテゴリ')} required>
                {(controlProps) => (
                  <Input
                    {...controlProps}
                    value={category}
                    onChange={(event) => {
                      setCategory(event.target.value);
                      setFormError(null);
                    }}
                    placeholder={t('drbfm.cadLink.labelEditor.categoryPlaceholder', '例: 回転・嵌合')}
                  />
                )}
              </Field>
              <Field label={t('drbfm.cadLink.labelEditor.note', 'メモ')}>
                {(controlProps) => (
                  <Textarea
                    {...controlProps}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    rows={2}
                  />
                )}
              </Field>
              {formError && (
                <Alert variant="warning">
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              )}
              <Button
                variant="outline"
                onClick={handleAddLabel}
                disabled={!selectedPosition || !labelName.trim() || !category.trim()}
              >
                <MapPin />
                {t('drbfm.cadLink.labelEditor.add', '部位を追加')}
              </Button>

              <RegisteredPartLabelsList labels={labels} onDelete={handleDeleteLabel} />
            </div>
          </div>
        </div>
        <DialogFooter className="p-6 pt-4">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel', 'キャンセル')}
          </Button>
          <Button
            onClick={handleApply}
            disabled={labels.length === 0 && initialLabels.length === 0}
          >
            {t('drbfm.cadLink.labelEditor.apply', 'ラベルを反映')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CADPartLabelEditorDialog;
